// Inspired by React Spectrum's loading announcement pattern for buttons.
// @see https://github.com/adobe/react-spectrum/blob/%40react-spectrum/button%403.17.6/packages/react-aria-components/src/Button.tsx#L135
import { announce } from '@react-aria/live-announcer';
import { useId } from '@react-aria/utils';
import { useEffect, useRef } from 'react';

// ─── aria-labelledby computation ───

interface UseLoadingAriaLabelledbyProps {
  /** The ID of the element (should be generated by the component) */
  id: string;
  /** Whether the element is in a loading state */
  isLoading: boolean;
  /** Existing aria-labelledby attribute from element props */
  ariaLabelledby?: string;
  /** Existing aria-label attribute from element props */
  ariaLabel?: string;
}

interface UseLoadingAriaLabelledbyReturn {
  /** The unique ID for the loading indicator (spinner) */
  loadingIndicatorId: string;
  /** The computed aria-labelledby value that includes loading indicator reference when loading */
  ariaLabelledby: string | undefined;
}

/**
 * Computes `aria-labelledby` that references the loading indicator when loading.
 *
 * @example
 * ```tsx
 * const rootId = useId(props.id);
 * const { loadingIndicatorId, ariaLabelledby } = useLoadingAriaLabelledby({
 *   id: rootId,
 *   isLoading,
 *   ariaLabelledby: props['aria-labelledby'],
 *   ariaLabel: props['aria-label'],
 * });
 * ```
 */
export function useLoadingAriaLabelledby({
  id: elementId,
  isLoading,
  ariaLabelledby: existingAriaLabelledby,
  ariaLabel,
}: UseLoadingAriaLabelledbyProps): UseLoadingAriaLabelledbyReturn {
  const loadingIndicatorId = useId();

  let ariaLabelledby = existingAriaLabelledby;
  if (isLoading) {
    // aria-labelledby wins over aria-label per W3C spec
    // https://www.w3.org/TR/accname-1.2/#computation-steps
    if (existingAriaLabelledby) {
      ariaLabelledby = `${existingAriaLabelledby} ${loadingIndicatorId}`;
    } else if (ariaLabel) {
      ariaLabelledby = `${elementId} ${loadingIndicatorId}`;
    }
  }

  return { loadingIndicatorId, ariaLabelledby };
}

// ─── Announcement effect ───

interface UseLoadingAnnouncementProps {
  /** The ID of the element */
  elementId: string;
  /** Whether the element is in a loading state */
  isLoading: boolean;
  /** Whether the element is currently focused */
  isFocused: boolean;
  /** The computed aria-labelledby (from useLoadingAriaLabelledby) */
  ariaLabelledby: string | undefined;
}

/**
 * Announces loading state changes to screen readers when the element is focused.
 * Call inside a component that has access to `isFocused` (e.g. via render prop).
 */
export function useLoadingAnnouncement({
  elementId,
  isLoading,
  isFocused,
  ariaLabelledby,
}: UseLoadingAnnouncementProps): void {
  const wasPending = useRef(isLoading);
  useEffect(() => {
    const message = { 'aria-labelledby': ariaLabelledby || elementId };

    if (!wasPending.current && isFocused && isLoading) {
      announce(message, 'assertive');
    } else if (wasPending.current && isFocused && !isLoading) {
      announce(message, 'assertive');
    }

    wasPending.current = isLoading;
  }, [isLoading, isFocused, ariaLabelledby, elementId]);
}
